import dayjs from 'dayjs'
import { prepareDataForValidation } from 'formik'
import { clone, get, pick, set } from 'lodash'
import { nanoid } from 'nanoid'
import * as yup from 'yup'

import { AGENT_MERGE_FIELDS } from '~/features/agents/constants'
import { EAgentKeys, TAgent } from '~/features/agents/models'
import { CAMPAIGN_BUSINESS_NAME_ERROR_MESSAGE } from '~/features/campaigns/components/campaignBuilder/constants'
import {
  messageContainsAnyBlockedShortener,
  messageStartContainsBusinessName,
  scheduleStepSendTimeToPayload,
} from '~/features/campaigns/components/campaignBuilder/utils'
import {
  ECampaignAgentAccessScope,
  ECampaignAgentJunctionStatus,
  ECampaignBuilderFormKeys,
  ECampaignBuilderStepIds,
  ECampaignChannel,
  ECampaignExecutionMode,
  ECampaignKeys,
  ECampaignScheduleStepKeys,
  ECampaignType,
  EMessageStrategy,
  EOfuCadence,
  EOfuOutboundEmailProvider,
  EScheduleStepOffsetUnit,
  EStructuredTimingMode,
  TCampaignAgentPayload,
  TCampaignAgentJunction,
  TCampaign,
  TCampaignBuilderForm,
  TCampaignScheduleStepForm,
  TCampaignUpdatePayload,
  TMergeField,
  TReplaceCampaignScheduleStepPayload,
  TReplaceCampaignScheduleStepsPayload,
  TSharedCampaignAgentSetting,
} from '~/features/campaigns/models'
import {
  appendRequiredAgentMetadataToPayload,
  getAgentMetadataVariable,
  getValueByNormalizedVariable,
} from '~/features/campaigns/utils/agentMetadata'
import {
  getCampaignAssistantJunctions,
  getCampaignPrimaryAssistantJunction,
} from '~/features/campaigns/utils/campaignAssistants'
import {
  MAX_LENGTH_EMAIL_CONTENT_MESSAGE,
  MAX_LENGTH_EMAIL_SUBJECT_MESSAGE,
  MAX_LENGTH_PHONE_INTRO_MESSAGE,
  MAX_LENGTH_PHONE_VOICE_MESSAGE,
  MAX_LENGTH_TEXT_MESSAGE,
  MAX_REACHOUTS_LIMIT,
  MIN_REACHOUTS,
  OUTREACH_TIME_ASAP_VALUE,
  SCHEDULE_STEP_MAX_LENGTH_CALL_SCRIPT,
  SCHEDULE_STEP_MAX_LENGTH_EMAIL_SUBJECT,
  SCHEDULE_STEP_MAX_LENGTH_TEXT_MESSAGE,
  SCHEDULE_STEP_MAX_LENGTH_VOICEMAIL_SCRIPT,
} from '~/features/campaigns/utils/constants'
import { getOutreachInstructionsSaveFields } from '~/features/campaigns/utils/outreachInstructions'
import getChangedFields from '~/utils/form'

const CONNECTED_FIELDS: [string, string][] = [
  [
    ECampaignBuilderFormKeys.phoneIntroMessage,
    ECampaignBuilderFormKeys.phoneVoicemailMessage,
  ],
  [
    ECampaignBuilderFormKeys.emailContent,
    ECampaignBuilderFormKeys.emailSubject,
  ],
]

const emailContentSchema = yup
  .string()
  .max(
    MAX_LENGTH_EMAIL_CONTENT_MESSAGE,
    `Max ${MAX_LENGTH_EMAIL_CONTENT_MESSAGE} characters`,
  )
  .test(
    'content-when-subject',
    'Email content is required when email subject is provided',
    function contentWhenSubject(value) {
      const subject = this.parent?.[ECampaignBuilderFormKeys.emailSubject]
      if (subject && !value) return false
      return true
    },
  )

const emailSubjectSchema = yup
  .string()
  .max(
    MAX_LENGTH_EMAIL_SUBJECT_MESSAGE,
    `Max ${MAX_LENGTH_EMAIL_SUBJECT_MESSAGE} characters`,
  )
  .test(
    'subject-when-content',
    'Email subject is required when email content is provided',
    function subjectWhenContent(value) {
      const content = this.parent?.[ECampaignBuilderFormKeys.emailContent]
      if (content && !value) return false
      return true
    },
  )

const phoneIntroMessageSchema = yup
  .string()
  .max(
    MAX_LENGTH_PHONE_INTRO_MESSAGE,
    `Max ${MAX_LENGTH_PHONE_INTRO_MESSAGE} characters`,
  )
  .test(
    'intro-when-voicemail',
    'Phone intro is required when voicemail is provided',
    function introWhenVoicemail(value) {
      const voicemail =
        this.parent?.[ECampaignBuilderFormKeys.phoneVoicemailMessage]
      if (voicemail && !value) return false
      return true
    },
  )

const phoneVoicemailMessageSchema = yup
  .string()
  .max(
    MAX_LENGTH_PHONE_VOICE_MESSAGE,
    `Max ${MAX_LENGTH_PHONE_VOICE_MESSAGE} characters`,
  )
  .test(
    'voicemail-when-intro',
    'Voicemail is required when phone intro is provided',
    function voicemailWhenIntro(value) {
      const intro = this.parent?.[ECampaignBuilderFormKeys.phoneIntroMessage]
      if (intro && !value) return false
      return true
    },
  )

type TCampaignWithAgentFallback = TCampaign & {
  Agent?: Pick<TAgent, EAgentKeys.businessName> | null
}

type TValidationSchemaContext = {
  selectedAgent?: TAgent | null
  campaign?: TCampaign | null
}

function getBusinessNameFromValidationContext(
  context: TValidationSchemaContext | undefined,
): string {
  const campaign = context?.campaign as TCampaignWithAgentFallback | null
  const campaignPrimaryJunction = getCampaignPrimaryAssistantJunction(campaign)
  const businessNameFromJunction =
    campaignPrimaryJunction?.Agent?.businessName || ''
  if (businessNameFromJunction.trim()) return businessNameFromJunction

  const businessNameFromCampaignAgent = String(
    campaign?.Agent?.[EAgentKeys.businessName] ||
      campaign?.Agent?.businessName ||
      '',
  )
  if (businessNameFromCampaignAgent.trim()) return businessNameFromCampaignAgent

  return String(context?.selectedAgent?.[EAgentKeys.businessName] || '')
}

const applyTextMessageValidations = ({
  schema,
}: {
  schema: yup.StringSchema<string | null | undefined>
}) =>
  schema
    .test(
      'shorteners',
      'Public URL shorteners aren’t allowed. Use your branded link or full website URL instead.',
      (value) => {
        if (!value) return true

        return !messageContainsAnyBlockedShortener(value)
      },
    )
    .test(
      'businessName',
      CAMPAIGN_BUSINESS_NAME_ERROR_MESSAGE,
      (value, testContext) => {
        const context = testContext.options?.context as
          | TValidationSchemaContext
          | undefined
        const rawBusinessName = getBusinessNameFromValidationContext(context)

        if (!value || !rawBusinessName?.trim()) return true

        return messageStartContainsBusinessName(value, rawBusinessName, {
          businessNameMergeFields: [
            EAgentKeys.businessName,
            EAgentKeys.businessName.replace(
              /[A-Z]/g,
              (match) => `-${match.toLowerCase()}`,
            ),
          ],
        })
      },
    )

const textMessageSchema = yup
  .string()
  .max(MAX_LENGTH_TEXT_MESSAGE, `Max ${MAX_LENGTH_TEXT_MESSAGE} characters`)

const baseValidationSchema = yup.object().shape({
  [ECampaignBuilderFormKeys.agentAccessScope]: yup
    .string()
    .oneOf(Object.values(ECampaignAgentAccessScope))
    .required(),
  [ECampaignBuilderFormKeys.sharedCampaignAgentIds]: yup.array(
    yup.string().required(),
  ),
  [ECampaignBuilderFormKeys.sharedCampaignAgentSettings]: yup.object(),
  [ECampaignBuilderFormKeys.additionalInstructions]: yup.string(),
  [ECampaignBuilderFormKeys.agentMetadataKeys]: yup.array(yup.string()),
  [ECampaignBuilderFormKeys.emailProvider]: yup.string(),
  [ECampaignBuilderFormKeys.emailProviderId]: yup
    .string()
    .when(ECampaignBuilderFormKeys.emailProvider, {
      is: (value: EOfuOutboundEmailProvider) =>
        value !== EOfuOutboundEmailProvider.internal,
      then: (schema) => schema.required('Email provider is required'),
    }),
  [ECampaignBuilderFormKeys.knowledge]: yup.array(
    yup.object({
      id: yup.string().optional().nullable(),
      text: yup.string(),
    }),
  ),
  [ECampaignBuilderFormKeys.mergeFields]: yup.array(
    yup.object({
      friendlyName: yup.string(),
      required: yup.boolean().nullable(),
      id: yup.string().optional().nullable(),
      variable: yup.string().optional().nullable(),
    }),
  ),
  [ECampaignBuilderFormKeys.ofu_channels]: yup
    .array()
    .of(yup.string().oneOf(Object.values(ECampaignChannel)))
    .min(1, 'At least one channel is required'),
  [ECampaignBuilderFormKeys.shouldRedirectToManageCampaign]: yup.boolean(),
  [ECampaignBuilderFormKeys.title]: yup
    .string()
    .min(3, 'Title must be at least 3 characters')
    .required('Title is required'),
})

export const structuredScheduleValidationSchema = yup.object().shape({
  [ECampaignBuilderFormKeys.useStructuredSchedule]: yup.boolean(),
  [ECampaignBuilderFormKeys.structuredTimingMode]: yup
    .string()
    .oneOf(Object.values(EStructuredTimingMode)),
  [ECampaignBuilderFormKeys.campaignScheduleSteps]: yup
    .array()
    .when(ECampaignBuilderFormKeys.useStructuredSchedule, {
      is: true,
      then: (arraySchema) =>
        arraySchema
          .of(
            yup
              .object()
              .shape(
                {
                  [ECampaignScheduleStepKeys.id]: yup.string().required(),
                  [ECampaignScheduleStepKeys.stepOrder]: yup
                    .number()
                    .required(),
                  [ECampaignScheduleStepKeys.offsetAmount]: yup
                    .number()
                    .integer()
                    .min(0)
                    .nullable(),
                  [ECampaignScheduleStepKeys.offsetUnit]: yup
                    .string()
                    .oneOf(Object.values(EScheduleStepOffsetUnit))
                    .nullable(),
                  [ECampaignScheduleStepKeys.scheduleDate]: yup
                    .string()
                    .nullable(),
                  [ECampaignScheduleStepKeys.sendTime]: yup.string().nullable(),
                  [ECampaignScheduleStepKeys.channel]: yup
                    .string()
                    .oneOf(Object.values(ECampaignChannel))
                    .nullable()
                    .test(
                      'schedule-step-channel-in-communication',
                      'This channel is not selected in Communication Channel. Enable it there or pick a different channel for this step.',
                      (value, context) => {
                        if (value == null) return true

                        const rootValues = context.from?.at(-1)?.value as
                          | Partial<TCampaignBuilderForm>
                          | undefined

                        const channels =
                          rootValues?.[ECampaignBuilderFormKeys.ofu_channels]

                        if (!Array.isArray(channels)) return true

                        return channels.includes(value as ECampaignChannel)
                      },
                    ),
                  [ECampaignScheduleStepKeys.textMessage]: yup
                    .string()
                    .nullable()
                    .when(ECampaignScheduleStepKeys.channel, {
                      is: ECampaignChannel.text,
                      then: (schema) =>
                        applyTextMessageValidations({
                          schema: schema.max(
                            SCHEDULE_STEP_MAX_LENGTH_TEXT_MESSAGE,
                            `Max ${SCHEDULE_STEP_MAX_LENGTH_TEXT_MESSAGE} characters`,
                          ),
                        }),
                    }),
                  [ECampaignScheduleStepKeys.emailSubject]: yup
                    .string()
                    .nullable()
                    .when(ECampaignScheduleStepKeys.channel, {
                      is: ECampaignChannel.email,
                      then: (schema) =>
                        schema
                          .max(
                            SCHEDULE_STEP_MAX_LENGTH_EMAIL_SUBJECT,
                            `Max ${SCHEDULE_STEP_MAX_LENGTH_EMAIL_SUBJECT} characters`,
                          )
                          .test(
                            'subject-when-body',
                            'Email subject is required when email body is provided',
                            (value, context) => {
                              const body =
                                context.parent?.[
                                  ECampaignScheduleStepKeys.emailBody
                                ]
                              if (body && !value) return false
                              return true
                            },
                          ),
                    }),
                  [ECampaignScheduleStepKeys.emailBody]: yup
                    .string()
                    .nullable()
                    .when(ECampaignScheduleStepKeys.channel, {
                      is: ECampaignChannel.email,
                      then: (schema) =>
                        schema.test(
                          'body-when-subject',
                          'Email body is required when email subject is provided',
                          (value, context) => {
                            const subject =
                              context.parent?.[
                                ECampaignScheduleStepKeys.emailSubject
                              ]
                            if (subject && !value) return false
                            return true
                          },
                        ),
                    }),
                  [ECampaignScheduleStepKeys.callScript]: yup
                    .string()
                    .nullable()
                    .when(ECampaignScheduleStepKeys.channel, {
                      is: ECampaignChannel.phone,
                      then: (schema) =>
                        schema
                          .max(
                            SCHEDULE_STEP_MAX_LENGTH_CALL_SCRIPT,
                            `Max ${SCHEDULE_STEP_MAX_LENGTH_CALL_SCRIPT} characters`,
                          )
                          .test(
                            'script-when-voicemail',
                            'Call script is required when voicemail is provided',
                            (value, context) => {
                              const voicemail =
                                context.parent?.[
                                  ECampaignScheduleStepKeys.voicemailScript
                                ]
                              if (voicemail && !value) return false
                              return true
                            },
                          ),
                    }),
                  [ECampaignScheduleStepKeys.voicemailScript]: yup
                    .string()
                    .nullable()
                    .when(ECampaignScheduleStepKeys.channel, {
                      is: ECampaignChannel.phone,
                      then: (schema) =>
                        schema
                          .max(
                            SCHEDULE_STEP_MAX_LENGTH_VOICEMAIL_SCRIPT,
                            `Max ${SCHEDULE_STEP_MAX_LENGTH_VOICEMAIL_SCRIPT} characters`,
                          )
                          .test(
                            'voicemail-when-script',
                            'Voicemail is required when call script is provided',
                            (value, context) => {
                              const callScript =
                                context.parent?.[
                                  ECampaignScheduleStepKeys.callScript
                                ]
                              if (callScript && !value) return false
                              return true
                            },
                          ),
                    }),
                  [ECampaignScheduleStepKeys.outreachInstructionsText]: yup
                    .string()
                    .nullable(),
                },
                [
                  [
                    ECampaignScheduleStepKeys.emailBody,
                    ECampaignScheduleStepKeys.emailSubject,
                  ],
                  [
                    ECampaignScheduleStepKeys.callScript,
                    ECampaignScheduleStepKeys.voicemailScript,
                  ],
                ],
              )
              .test(
                'schedule-step-offset-pair',
                'Wait amount and unit must be set together',
                (step) => {
                  if (!step) return true

                  const amountMissing =
                    step[ECampaignScheduleStepKeys.offsetAmount] === null ||
                    step[ECampaignScheduleStepKeys.offsetAmount] === undefined
                  const unitMissing =
                    step[ECampaignScheduleStepKeys.offsetUnit] === null ||
                    step[ECampaignScheduleStepKeys.offsetUnit] === undefined

                  return amountMissing === unitMissing
                },
              )
              .test(
                'schedule-step-send-time-needs-day',
                'A send time needs a wait offset or a fixed date',
                (step) => {
                  if (!step) return true

                  const sendTime = step[ECampaignScheduleStepKeys.sendTime]
                  const isAiOrEmpty =
                    sendTime === null ||
                    sendTime === undefined ||
                    sendTime === ''

                  if (isAiOrEmpty) return true

                  const hasOffset =
                    step[ECampaignScheduleStepKeys.offsetAmount] !== null &&
                    step[ECampaignScheduleStepKeys.offsetAmount] !== undefined
                  const hasScheduleDate = Boolean(
                    step[ECampaignScheduleStepKeys.scheduleDate],
                  )

                  return hasOffset || hasScheduleDate
                },
              )
              .test(
                'schedule-step-substantive',
                'A step must define at least one of: timing, channel, content, or instructions',
                (step) => {
                  if (!step) return true

                  const substantiveFields = [
                    ECampaignScheduleStepKeys.offsetAmount,
                    ECampaignScheduleStepKeys.scheduleDate,
                    ECampaignScheduleStepKeys.sendTime,
                    ECampaignScheduleStepKeys.channel,
                    ECampaignScheduleStepKeys.textMessage,
                    ECampaignScheduleStepKeys.callScript,
                    ECampaignScheduleStepKeys.voicemailScript,
                    ECampaignScheduleStepKeys.emailSubject,
                    ECampaignScheduleStepKeys.emailBody,
                    ECampaignScheduleStepKeys.outreachInstructionsText,
                  ] as const

                  return substantiveFields.some((field) => {
                    const value = step[field]
                    return value !== undefined && value !== null && value !== ''
                  })
                },
              ),
          )
          .min(1, 'Add at least one step before simulating or going live')
          .test(
            'schedule-steps-relative-no-dates',
            'Fixed dates are not allowed when using wait between outreaches',
            function relativeNoDates(steps) {
              const timingMode =
                this.parent?.[ECampaignBuilderFormKeys.structuredTimingMode]
              if (
                timingMode !== EStructuredTimingMode.relative ||
                !Array.isArray(steps)
              ) {
                return true
              }

              return steps.every(
                (step) =>
                  !step ||
                  step[ECampaignScheduleStepKeys.scheduleDate] == null ||
                  step[ECampaignScheduleStepKeys.scheduleDate] === '',
              )
            },
          )
          .test(
            'schedule-steps-absolute-no-offsets',
            'Wait offsets are not allowed when using fixed dates',
            function absoluteNoOffsets(steps) {
              const timingMode =
                this.parent?.[ECampaignBuilderFormKeys.structuredTimingMode]
              if (
                timingMode !== EStructuredTimingMode.absolute ||
                !Array.isArray(steps)
              ) {
                return true
              }

              return steps.every(
                (step) =>
                  !step ||
                  (step[ECampaignScheduleStepKeys.offsetAmount] == null &&
                    step[ECampaignScheduleStepKeys.offsetUnit] == null),
              )
            },
          )
          .test(
            'schedule-steps-instant-offset-timing',
            'Minute and hour waits cannot use a send time',
            function instantOffsetTiming(steps) {
              const timingMode =
                this.parent?.[ECampaignBuilderFormKeys.structuredTimingMode]
              if (
                timingMode !== EStructuredTimingMode.relative ||
                !Array.isArray(steps)
              ) {
                return true
              }

              // `0 minutes` / `0 hours` are valid (the builder's own helper
              // text recommends them for an immediate or right-after send).
              // The only remaining constraint is that these units can't also
              // carry a pinned send time.
              const violationIndex = steps.findIndex((step) => {
                if (!step) return false

                const unit = step[ECampaignScheduleStepKeys.offsetUnit]
                if (
                  unit !== EScheduleStepOffsetUnit.minutes &&
                  unit !== EScheduleStepOffsetUnit.hours
                ) {
                  return false
                }

                const sendTime = step[ECampaignScheduleStepKeys.sendTime]
                return sendTime != null && sendTime !== ''
              })

              if (violationIndex === -1) return true

              return this.createError({
                path: `${this.path}[${violationIndex}]`,
                message: 'Minute and hour waits cannot use a send time',
              })
            },
          )
          .test(
            'schedule-steps-same-day-order',
            'A step with no wait needs a send time later than the previous step',
            function sameDayOrder(steps) {
              const timingMode =
                this.parent?.[ECampaignBuilderFormKeys.structuredTimingMode]
              if (
                timingMode !== EStructuredTimingMode.relative ||
                !Array.isArray(steps)
              ) {
                return true
              }

              // `.findIndex` rather than a `for` loop: this repo's eslint
              // config bans `continue` (`no-continue`), and the sibling tests
              // in this chain already use `steps.every(...)`.
              const violationIndex = steps.findIndex((step, index) => {
                // Step 0 anchors on enrollment, not on a previous step.
                if (index === 0 || !step) return false

                const unit = step[ECampaignScheduleStepKeys.offsetUnit]
                const amount = step[ECampaignScheduleStepKeys.offsetAmount]
                const isSameDayStep =
                  amount === 0 &&
                  (unit === EScheduleStepOffsetUnit.days ||
                    unit === EScheduleStepOffsetUnit.weeks)

                if (!isSameDayStep) return false

                const previous = steps[index - 1]
                const currentMinutes = scheduleStepSendTimeToPayload(
                  step[ECampaignScheduleStepKeys.sendTime],
                )
                const previousMinutes = scheduleStepSendTimeToPayload(
                  previous?.[ECampaignScheduleStepKeys.sendTime],
                )

                // An unpinned neighbour makes the ordering unprovable: the
                // previous send time would come from the AI or from enrollment.
                if (currentMinutes === null || previousMinutes === null) {
                  return true
                }
                return currentMinutes <= previousMinutes
              })

              if (violationIndex === -1) return true

              return this.createError({
                path: `${this.path}[${violationIndex}]`,
                message:
                  'A step with no wait needs a send time later than the previous step',
              })
            },
          )
          .test(
            'schedule-steps-absolute-increasing',
            'Fixed dates must move forward from one step to the next',
            function absoluteIncreasing(steps) {
              const timingMode =
                this.parent?.[ECampaignBuilderFormKeys.structuredTimingMode]
              if (
                timingMode !== EStructuredTimingMode.absolute ||
                !Array.isArray(steps)
              ) {
                return true
              }

              // Compared against the most recent *dated* step, not the
              // immediate predecessor: a dateless step in between pins
              // nothing at runtime (the AI chooses it), so skipping it here —
              // instead of resetting the anchor to it — is what keeps a
              // genuine reversal across it from slipping through.
              //
              // `.findIndex` rather than a `for` loop: this repo's eslint
              // config bans `continue` (`no-continue`), and the sibling tests
              // in this chain already use `steps.every(...)`.
              let lastDatedDay: ReturnType<typeof dayjs> | null = null
              let lastDatedMinutes: number | null = null

              const violationIndex = steps.findIndex((current) => {
                if (!current) return false

                const currentDate =
                  current[ECampaignScheduleStepKeys.scheduleDate]

                // A missing date is an incomplete step, not a backwards one —
                // skip it without disturbing the running anchor.
                if (!currentDate) return false

                const currentDay = dayjs(currentDate).startOf('day')
                if (!currentDay.isValid()) return false

                const currentMinutes = scheduleStepSendTimeToPayload(
                  current[ECampaignScheduleStepKeys.sendTime],
                )

                if (!lastDatedDay) {
                  lastDatedDay = currentDay
                  lastDatedMinutes = currentMinutes
                  return false
                }

                if (currentDay.isBefore(lastDatedDay)) return true

                if (currentDay.isSame(lastDatedDay)) {
                  // Same day: the pinned times must separate the two steps.
                  const isViolation =
                    currentMinutes === null ||
                    lastDatedMinutes === null ||
                    currentMinutes <= lastDatedMinutes
                  lastDatedDay = currentDay
                  lastDatedMinutes = currentMinutes
                  return isViolation
                }

                // Different day, moving forward — resets the anchor.
                lastDatedDay = currentDay
                lastDatedMinutes = currentMinutes
                return false
              })

              if (violationIndex === -1) return true

              return this.createError({
                path: `${this.path}[${violationIndex}]`,
                message:
                  'Fixed dates must move forward from one step to the next',
              })
            },
          ),
    }),
})

const baseGoalValidationSchema = yup.string().max(5000, 'Max 5000 characters')

const simpleCampaignValidationSchema = yup
  .object()
  .shape(
    {
      [ECampaignBuilderFormKeys.description]: yup.string(),
      [ECampaignBuilderFormKeys.emailContent]: emailContentSchema.when(
        [
          ECampaignBuilderFormKeys.messageStrategy,
          ECampaignBuilderFormKeys.ofu_channels,
        ],
        {
          is: (
            messageStrategy: EMessageStrategy,
            channels: ECampaignChannel[],
          ) =>
            messageStrategy === EMessageStrategy.userDefined &&
            channels.includes(ECampaignChannel.email),
          then: (schema) => schema.required('Email content is required'),
        },
      ),
      [ECampaignBuilderFormKeys.emailSubject]: emailSubjectSchema.when(
        [
          ECampaignBuilderFormKeys.messageStrategy,
          ECampaignBuilderFormKeys.ofu_channels,
        ],
        {
          is: (
            messageStrategy: EMessageStrategy,
            channels: ECampaignChannel[],
          ) =>
            messageStrategy === EMessageStrategy.userDefined &&
            channels.includes(ECampaignChannel.email),
          then: (schema) => schema.required('Email subject is required'),
        },
      ),
      [ECampaignBuilderFormKeys.executionMode]: yup
        .string()
        .oneOf(Object.values(ECampaignExecutionMode))
        .nullable(),
      [ECampaignBuilderFormKeys.messageStrategy]: yup
        .string()
        .oneOf(Object.values(EMessageStrategy))
        .required('Message strategy is required'),
      [ECampaignBuilderFormKeys.ofuGoal]:
        baseGoalValidationSchema.required('Goal is required'),
      [ECampaignBuilderFormKeys.ofuIsManual]: yup.boolean().nullable(),
      [ECampaignBuilderFormKeys.ofu_outreachTime]: yup.string(),
      [ECampaignBuilderFormKeys.phoneIntroMessage]:
        phoneIntroMessageSchema.when(
          [
            ECampaignBuilderFormKeys.messageStrategy,
            ECampaignBuilderFormKeys.ofu_channels,
          ],
          {
            is: (
              messageStrategy: EMessageStrategy,
              channels: ECampaignChannel[],
            ) =>
              messageStrategy === EMessageStrategy.userDefined &&
              channels.includes(ECampaignChannel.phone),
            then: (schema) => schema.required('Phone intro is required'),
          },
        ),
      [ECampaignBuilderFormKeys.phoneVoicemailMessage]:
        phoneVoicemailMessageSchema.when(
          [
            ECampaignBuilderFormKeys.messageStrategy,
            ECampaignBuilderFormKeys.ofu_channels,
          ],
          {
            is: (
              messageStrategy: EMessageStrategy,
              channels: ECampaignChannel[],
            ) =>
              messageStrategy === EMessageStrategy.userDefined &&
              channels.includes(ECampaignChannel.phone),
            then: (schema) => schema.required('Voicemail is required'),
          },
        ),
      [ECampaignBuilderFormKeys.startDate]: yup
        .string()
        .when(ECampaignBuilderFormKeys.executionMode, {
          is: ECampaignExecutionMode.scheduled,
          then: (schema) => schema.required('Scheduled date is required'),
        }),
      [ECampaignBuilderFormKeys.textMessage]: textMessageSchema.when(
        [
          ECampaignBuilderFormKeys.messageStrategy,
          ECampaignBuilderFormKeys.ofu_channels,
        ],
        {
          is: (
            messageStrategy: EMessageStrategy,
            channels: ECampaignChannel[],
          ) =>
            messageStrategy === EMessageStrategy.userDefined &&
            channels.includes(ECampaignChannel.text),
          then: (schema) =>
            applyTextMessageValidations({
              schema: schema.required('Text message is required'),
            }),
        },
      ),
    },
    CONNECTED_FIELDS,
  )
  .concat(baseValidationSchema)

const ongoingCampaignValidationSchema = yup
  .object()
  .shape(
    {
      [ECampaignBuilderFormKeys.cadence]: yup
        .string()
        .oneOf(Object.values(EOfuCadence)),
      [ECampaignBuilderFormKeys.cadenceCustom]: yup
        .string()
        .when(
          [
            ECampaignBuilderFormKeys.useStructuredSchedule,
            ECampaignBuilderFormKeys.cadence,
          ],
          {
            is: (useStructuredSchedule: boolean, cadence: EOfuCadence) =>
              !useStructuredSchedule && cadence === EOfuCadence.custom,
            then: (schema) =>
              schema.required('Custom cadence schedule is required'),
            otherwise: (schema) => schema.nullable(),
          },
        ),
      [ECampaignBuilderFormKeys.channelOfFirstOutreach]: yup
        .string()
        .oneOf(Object.values(ECampaignChannel))
        .nullable(),
      [ECampaignBuilderFormKeys.contactEligibilityCriteria]: yup.string(),
      [ECampaignBuilderFormKeys.emailContent]: yup
        .string()
        .when(ECampaignBuilderFormKeys.useStructuredSchedule, {
          is: true,
          then: () => yup.string(),
          otherwise: () => emailContentSchema,
        }),
      [ECampaignBuilderFormKeys.emailSubject]: yup
        .string()
        .when(ECampaignBuilderFormKeys.useStructuredSchedule, {
          is: true,
          then: () => yup.string(),
          otherwise: () => emailSubjectSchema,
        }),
      [ECampaignBuilderFormKeys.endDate]: yup
        .string()
        .nullable()
        .test(
          'endDate',
          'End date must be greater than start date',
          (endDate, context) => {
            if (!endDate) return true

            const {
              options: { context: formValues },
            } = context

            const { startDate } = formValues as TCampaignBuilderForm

            if (!startDate) return false

            const isEndDateBeforeStartDate = dayjs(endDate).isBefore(
              dayjs(startDate),
              'day',
            )

            return !isEndDateBeforeStartDate
          },
        ),
      [ECampaignBuilderFormKeys.useStructuredSchedule]: yup.boolean(),
      [ECampaignBuilderFormKeys.maxReachouts]: yup
        .number()
        .min(MIN_REACHOUTS, 'Max reach outs is required')
        .max(MAX_REACHOUTS_LIMIT, `Max ${MAX_REACHOUTS_LIMIT} reach outs`)
        .when(ECampaignBuilderFormKeys.useStructuredSchedule, {
          is: true,
          then: (schema) => schema.nullable().notRequired(),
          otherwise: (schema) => schema.required('Max reach outs is required'),
        }),
      [ECampaignBuilderFormKeys.ofu_outreachTime]: yup.string(),
      [ECampaignBuilderFormKeys.ofuGoal]:
        baseGoalValidationSchema.required('Goal is required'),
      [ECampaignBuilderFormKeys.phoneIntroMessage]: yup
        .string()
        .when(ECampaignBuilderFormKeys.useStructuredSchedule, {
          is: true,
          then: () => yup.string(),
          otherwise: () => phoneIntroMessageSchema,
        }),
      [ECampaignBuilderFormKeys.phoneVoicemailMessage]: yup
        .string()
        .when(ECampaignBuilderFormKeys.useStructuredSchedule, {
          is: true,
          then: () => yup.string(),
          otherwise: () => phoneVoicemailMessageSchema,
        }),
      [ECampaignBuilderFormKeys.startDate]: yup
        .string()
        .required('Start date is required'),
      [ECampaignBuilderFormKeys.textMessage]: textMessageSchema.when(
        [
          ECampaignBuilderFormKeys.useStructuredSchedule,
          ECampaignBuilderFormKeys.channelOfFirstOutreach,
          ECampaignBuilderFormKeys.ofu_channels,
        ],
        {
          is: (
            useStructuredSchedule: boolean,
            channelOfFirstOutreach: ECampaignChannel,
            channels: ECampaignChannel[],
          ) =>
            !useStructuredSchedule &&
            channelOfFirstOutreach === ECampaignChannel.text &&
            channels.includes(ECampaignChannel.text),
          then: (schema) => applyTextMessageValidations({ schema }),
        },
      ),
    },
    CONNECTED_FIELDS,
  )
  .concat(baseValidationSchema)
  .concat(structuredScheduleValidationSchema)

const baseSaveValidationSchema = yup.object().shape({
  [ECampaignBuilderFormKeys.title]: yup
    .string()
    .min(3, 'Title must be at least 3 characters')
    .required('Title is required'),
})

const ofuSaveValidationSchema = yup
  .object()
  .shape({
    [ECampaignBuilderFormKeys.textMessage]: yup
      .string()
      .when(
        [
          ECampaignBuilderFormKeys.useStructuredSchedule,
          ECampaignBuilderFormKeys.channelOfFirstOutreach,
          ECampaignBuilderFormKeys.ofu_channels,
        ],
        {
          is: (
            useStructuredSchedule: boolean,
            channelOfFirstOutreach: ECampaignChannel,
            channels: ECampaignChannel[],
          ) => {
            const hasTextChannelSelected = channels.includes(
              ECampaignChannel.text,
            )

            return (
              !useStructuredSchedule &&
              channelOfFirstOutreach === ECampaignChannel.text &&
              hasTextChannelSelected
            )
          },
          then: (schema) => applyTextMessageValidations({ schema }),
        },
      ),
  })
  .concat(baseSaveValidationSchema)

const ofuOneTimeSaveValidationSchema = yup
  .object()
  .shape({
    [ECampaignBuilderFormKeys.textMessage]: yup
      .string()
      .when(
        [
          ECampaignBuilderFormKeys.messageStrategy,
          ECampaignBuilderFormKeys.ofu_channels,
        ],
        {
          is: (
            messageStrategy: EMessageStrategy,
            channels: ECampaignChannel[],
          ) => {
            const hasTextChannelSelected = channels.includes(
              ECampaignChannel.text,
            )

            return (
              messageStrategy === EMessageStrategy.userDefined &&
              hasTextChannelSelected
            )
          },
          then: (schema) => applyTextMessageValidations({ schema }),
        },
      ),
  })
  .concat(baseSaveValidationSchema)

export const campaignBuilderValidationSchema = {
  [ECampaignType.simple]: simpleCampaignValidationSchema,
  [ECampaignType.ofu]: ongoingCampaignValidationSchema,
  [ECampaignType.ofuOneTime]: simpleCampaignValidationSchema,
} as const

export const saveValidationSchema = {
  [ECampaignType.simple]: simpleCampaignValidationSchema,
  [ECampaignType.ofu]: ofuSaveValidationSchema,
  [ECampaignType.ofuOneTime]: ofuOneTimeSaveValidationSchema,
} as const

export function getSchemaValidationErrors(
  values: TCampaignBuilderForm,
  campaignType: ECampaignType.ofu | ECampaignType.ofuOneTime,
  selectedAgent?: TAgent | null,
  campaign?: TCampaign | null,
): Record<string, string> {
  const schema = campaignBuilderValidationSchema[campaignType]

  if (!schema) return {}

  const preparedValues = prepareDataForValidation(
    values,
  ) as TCampaignBuilderForm

  try {
    schema.validateSync(preparedValues, {
      abortEarly: false,
      context: { ...preparedValues, selectedAgent, campaign },
    })
    return {}
  } catch (err) {
    if (err instanceof yup.ValidationError && err.inner?.length) {
      const errors: Record<string, string> = {}
      err.inner.forEach((e) => {
        if (e.path != null && e.message) {
          set(errors, e.path, e.message)
        }
      })
      return errors
    }
    return {}
  }
}

export type TRequiredFieldCandidate = {
  stepId: ECampaignBuilderStepIds
  pathField: string
  friendlyField: string
}

function getEmptyValueForRequiredCheck(pathField: string): undefined | [] {
  return pathField === ECampaignBuilderFormKeys.ofu_channels ||
    pathField === ECampaignBuilderFormKeys.campaignScheduleSteps
    ? []
    : undefined
}

const shouldKeepRequiredCandidate = ({
  campaignType,
  isRequiredPerSchemaWhenCleared,
  pathField,
  values,
}: {
  campaignType: ECampaignType
  isRequiredPerSchemaWhenCleared: boolean
  pathField: string
  values: TCampaignBuilderForm
}) => {
  const fieldValue = get(values, pathField)

  if (pathField === ECampaignBuilderFormKeys.textMessage) {
    const isOngoingCampaign = campaignType === ECampaignType.ofu
    const isAiDrivenCadence =
      !values[ECampaignBuilderFormKeys.useStructuredSchedule]
    const isFirstOutreachChannelText =
      values[ECampaignBuilderFormKeys.channelOfFirstOutreach] ===
      ECampaignChannel.text
    const shouldForceTextMessageAsRequired =
      isOngoingCampaign &&
      isAiDrivenCadence &&
      isFirstOutreachChannelText &&
      !!fieldValue

    return shouldForceTextMessageAsRequired || isRequiredPerSchemaWhenCleared
  }

  return isRequiredPerSchemaWhenCleared
}

export function getRequiredFieldsForCurrentValues(
  values: TCampaignBuilderForm,
  campaignType: ECampaignType.ofu | ECampaignType.ofuOneTime,
  candidates: TRequiredFieldCandidate[],
  selectedAgent?: TAgent | null,
  campaign?: TCampaign | null,
): TRequiredFieldCandidate[] {
  return candidates.filter(({ pathField }) => {
    const valuesWithFieldCleared = clone(values)
    set(
      valuesWithFieldCleared,
      pathField,
      getEmptyValueForRequiredCheck(pathField),
    )
    const errors = getSchemaValidationErrors(
      valuesWithFieldCleared as TCampaignBuilderForm,
      campaignType,
      selectedAgent,
      campaign,
    )

    return shouldKeepRequiredCandidate({
      campaignType,
      isRequiredPerSchemaWhenCleared: Boolean(get(errors, pathField)),
      pathField,
      values,
    })
  })
}

const BASE_CAMPAIGN_FIELDS = [
  ECampaignBuilderFormKeys.agentAccessScope,
  ECampaignBuilderFormKeys.sharedCampaignAgentIds,
  ECampaignBuilderFormKeys.sharedCampaignAgentSettings,
  ECampaignBuilderFormKeys.title,
  ECampaignBuilderFormKeys.description,
  ECampaignBuilderFormKeys.ofuGoal,
  ECampaignBuilderFormKeys.phoneIntroMessage,
  ECampaignBuilderFormKeys.phoneVoicemailMessage,
  ECampaignBuilderFormKeys.emailSubject,
  ECampaignBuilderFormKeys.emailContent,
  ECampaignBuilderFormKeys.textMessage,
  ECampaignBuilderFormKeys.startDate,
  ECampaignBuilderFormKeys.knowledge,
  ECampaignBuilderFormKeys.additionalInstructions,
  ECampaignBuilderFormKeys.ofu_outreachTime,
  ECampaignBuilderFormKeys.ofu_channels,
  ECampaignBuilderFormKeys.emailProvider,
  ECampaignBuilderFormKeys.emailProviderId,
  ECampaignBuilderFormKeys.emailProviderCustomEmailBody,
] as const

export { getAgentMetadataVariable, getValueByNormalizedVariable }

const SIMPLE_CAMPAIGN_FIELDS = [
  ECampaignBuilderFormKeys.messageStrategy,
  ECampaignBuilderFormKeys.ofuIsManual,
  ECampaignBuilderFormKeys.executionMode,
  ...BASE_CAMPAIGN_FIELDS,
] as const

const ONGOING_CAMPAIGN_FIELDS = [
  ECampaignBuilderFormKeys.endDate,
  ECampaignBuilderFormKeys.channelOfFirstOutreach,
  ECampaignBuilderFormKeys.maxReachouts,
  ECampaignBuilderFormKeys.cadence,
  ECampaignBuilderFormKeys.cadenceCustom,
  ECampaignBuilderFormKeys.contactEligibilityCriteria,
  ECampaignBuilderFormKeys.useStructuredSchedule,
  ECampaignBuilderFormKeys.structuredTimingMode,
  ...BASE_CAMPAIGN_FIELDS,
] as const

const SIMPLE_CAMPAIGN_FIELDS_DEFAULT_VALUES = [
  ECampaignBuilderFormKeys.executionMode,
]

const ONGOING_CAMPAIGN_FIELDS_DEFAULT_VALUES = [
  ECampaignBuilderFormKeys.contactEligibilityCriteria,
  ECampaignBuilderFormKeys.cadence,
  ECampaignBuilderFormKeys.maxReachouts,
]

const addDefaultValuesToPayload = <T extends object>({
  campaign,
  initialValues,
  isSimpleCampaign,
  payload,
}: {
  campaign?: TCampaign | null
  initialValues: TCampaignBuilderForm
  isSimpleCampaign: boolean
  payload: T
}) => {
  const fields = isSimpleCampaign
    ? SIMPLE_CAMPAIGN_FIELDS_DEFAULT_VALUES
    : ONGOING_CAMPAIGN_FIELDS_DEFAULT_VALUES

  const initialCampaignFormValues = pick(initialValues, fields)
  const initialCampaignValues = pick(campaign || {}, fields)

  const valuesWithDefaultApplied = getChangedFields({
    currentValues: initialCampaignFormValues,
    initialValues: initialCampaignValues,
  })

  const newPayload = {
    ...payload,
  }

  Object.keys(valuesWithDefaultApplied).forEach((key) => {
    if (key in payload) return // don't apply default when already change

    newPayload[key as keyof typeof newPayload] = valuesWithDefaultApplied[key]
  })

  return newPayload
}

function emptyNumberInputToNull(
  value: number | string | null | undefined,
): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) {
    return null
  }

  return numericValue
}

function transformBuilderPayloadForApi(
  payload: Record<string, unknown>,
  timezone: string,
): Record<string, unknown> {
  const next = { ...payload }
  const outreachTime = next[ECampaignBuilderFormKeys.ofu_outreachTime]
  const startDate = next[ECampaignBuilderFormKeys.startDate]
  const endDate = next[ECampaignBuilderFormKeys.endDate]
  const channelOfFirstOutreach =
    next[ECampaignBuilderFormKeys.channelOfFirstOutreach]

  const additionalInstructions =
    next[ECampaignBuilderFormKeys.additionalInstructions]

  const isAsapOutreach = outreachTime === OUTREACH_TIME_ASAP_VALUE

  if (outreachTime && !isAsapOutreach) {
    const [hours, minutes] = String(outreachTime).split(':')

    // Build an ISO-8601 string whose time portion exactly matches the selected
    // hour from the dropdown (no timezone conversion). Using UTC ensures
    // toISOString preserves the picked hour, e.g. "09:00" -> "...T09:00:00.000Z".
    next[ECampaignBuilderFormKeys.ofu_outreachTime] = dayjs
      .utc()
      .hour(parseInt(hours, 10))
      .minute(parseInt(minutes, 10))
      .second(0)
      .millisecond(0)
      .toISOString()
  }

  if (isAsapOutreach) {
    next[ECampaignBuilderFormKeys.ofu_outreachTime] = null
  }

  if (startDate) {
    // the logic is to avoid sending outreaches before 8 am so the start should be at 8am
    next[ECampaignBuilderFormKeys.startDate] = dayjs(
      new Date(startDate as string),
    )
      .tz(timezone)
      .hour(8)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toISOString()
  }

  // the logic is to avoid sending outreaches after 8 pm so the end should be at 8pm
  if (endDate) {
    next[ECampaignBuilderFormKeys.endDate] = dayjs(endDate as string)
      .tz(timezone)
      .hour(20)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toISOString()
  }

  if (ECampaignBuilderFormKeys.additionalInstructions in next) {
    Object.assign(
      next,
      getOutreachInstructionsSaveFields(String(additionalInstructions ?? '')),
    )
  }

  if (
    ECampaignBuilderFormKeys.channelOfFirstOutreach in next &&
    !channelOfFirstOutreach
  ) {
    next[ECampaignBuilderFormKeys.channelOfFirstOutreach] = null
  }

  if (ECampaignBuilderFormKeys.maxReachouts in next) {
    next[ECampaignBuilderFormKeys.maxReachouts] = emptyNumberInputToNull(
      next[ECampaignBuilderFormKeys.maxReachouts] as
        | number
        | string
        | null
        | undefined,
    )
  }

  const sharedAgentIds = next[
    ECampaignBuilderFormKeys.sharedCampaignAgentIds
  ] as string[] | undefined
  const sharedAgentSettings = next[
    ECampaignBuilderFormKeys.sharedCampaignAgentSettings
  ] as Record<string, TSharedCampaignAgentSetting> | undefined

  if (ECampaignBuilderFormKeys.sharedCampaignAgentSettings in next) {
    delete next[ECampaignBuilderFormKeys.sharedCampaignAgentSettings]
  }

  if (sharedAgentIds?.length) {
    next[ECampaignKeys.agents] = sharedAgentIds.map((agentId) => {
      const agentSetting = sharedAgentSettings?.[agentId]
      const trimmedRules = agentSetting?.customRules?.trim() ?? ''
      const trimmedKnowledge = agentSetting?.customKnowledge?.trim() ?? ''
      const parsedKnowledge = trimmedKnowledge
        .split('\n')
        .map((text) => text.trim())
        .filter(Boolean)
        .map((text) => ({ id: nanoid(), text }))
      const locationFields = Object.fromEntries(
        Object.entries(agentSetting?.locationFields ?? {}).flatMap(
          ([fieldKey, fieldValue]) => {
            if (typeof fieldValue !== 'string') {
              return []
            }
            const trimmedFieldValue = fieldValue.trim()
            if (!trimmedFieldValue) {
              return []
            }

            const variableKey = getAgentMetadataVariable(fieldKey)
            if (!variableKey) {
              return []
            }

            return [[variableKey, trimmedFieldValue]]
          },
        ),
      ) as Record<string, string>

      const agentPayload: TCampaignAgentPayload = { agentId }

      // Keep parity with campaignFacts behavior: when cleared, send empty
      // value so previously saved assistant knowledge/rules are removed
      // server-side (rather than the stale junction value surviving).
      agentPayload.agentCampaignKnowledge = parsedKnowledge
      agentPayload.agentCampaignRules = trimmedRules
      if (Object.keys(locationFields).length) {
        agentPayload.agentMetadata = locationFields
      }

      return agentPayload
    })
  }

  return next
}

function emptyScheduleStepStringToNull(
  value: string | null | undefined,
): string | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  return value
}

function toReplaceScheduleStepPayload(
  step: TCampaignScheduleStepForm,
  stepOrder: number,
  timingMode: EStructuredTimingMode,
): TReplaceCampaignScheduleStepPayload {
  const isAbsolute = timingMode === EStructuredTimingMode.absolute

  return {
    [ECampaignScheduleStepKeys.stepOrder]: stepOrder,
    // Absolute forbids offsets; relative forbids scheduleDate (BE refine).
    // Always send `null` (not omit) so Prisma clears any previous value.
    [ECampaignScheduleStepKeys.offsetAmount]: isAbsolute
      ? null
      : emptyNumberInputToNull(step[ECampaignScheduleStepKeys.offsetAmount]),
    [ECampaignScheduleStepKeys.offsetUnit]: isAbsolute
      ? null
      : (step[ECampaignScheduleStepKeys.offsetUnit] ?? null),
    [ECampaignScheduleStepKeys.scheduleDate]: isAbsolute
      ? (step[ECampaignScheduleStepKeys.scheduleDate] ?? null)
      : null,
    [ECampaignScheduleStepKeys.sendTime]: scheduleStepSendTimeToPayload(
      step[ECampaignScheduleStepKeys.sendTime],
    ),
    [ECampaignScheduleStepKeys.channel]:
      step[ECampaignScheduleStepKeys.channel] ?? null,
    [ECampaignScheduleStepKeys.textMessage]: emptyScheduleStepStringToNull(
      step[ECampaignScheduleStepKeys.textMessage],
    ),
    [ECampaignScheduleStepKeys.emailSubject]: emptyScheduleStepStringToNull(
      step[ECampaignScheduleStepKeys.emailSubject],
    ),
    [ECampaignScheduleStepKeys.emailBody]: emptyScheduleStepStringToNull(
      step[ECampaignScheduleStepKeys.emailBody],
    ),
    [ECampaignScheduleStepKeys.callScript]: emptyScheduleStepStringToNull(
      step[ECampaignScheduleStepKeys.callScript],
    ),
    [ECampaignScheduleStepKeys.voicemailScript]: emptyScheduleStepStringToNull(
      step[ECampaignScheduleStepKeys.voicemailScript],
    ),
    [ECampaignScheduleStepKeys.outreachInstructionsText]:
      emptyScheduleStepStringToNull(
        step[ECampaignScheduleStepKeys.outreachInstructionsText],
      ),
  }
}

export function mapCampaignScheduleStepsForApi(
  steps: TCampaignScheduleStepForm[],
  timingMode: EStructuredTimingMode,
): TReplaceCampaignScheduleStepPayload[] {
  return steps.map((step, index) =>
    toReplaceScheduleStepPayload(step, index, timingMode),
  )
}

/** `PUT /campaigns/:id/schedule-steps` body, or `null` when the call can be skipped. */
export function getCampaignScheduleStepsReplacePayload({
  initialValues,
  values,
}: {
  initialValues: TCampaignBuilderForm
  values: TCampaignBuilderForm
}): TReplaceCampaignScheduleStepsPayload | null {
  if (!values[ECampaignBuilderFormKeys.useStructuredSchedule]) {
    return null
  }

  const steps = values[ECampaignBuilderFormKeys.campaignScheduleSteps] ?? []
  const timingMode = values[ECampaignBuilderFormKeys.structuredTimingMode]

  const enabledStructured =
    !initialValues[ECampaignBuilderFormKeys.useStructuredSchedule]

  const changedScheduleFields = getChangedFields({
    currentValues: {
      [ECampaignBuilderFormKeys.structuredTimingMode]: timingMode,
      [ECampaignBuilderFormKeys.campaignScheduleSteps]: steps,
    },
    initialValues: {
      [ECampaignBuilderFormKeys.structuredTimingMode]:
        initialValues[ECampaignBuilderFormKeys.structuredTimingMode],
      [ECampaignBuilderFormKeys.campaignScheduleSteps]:
        initialValues[ECampaignBuilderFormKeys.campaignScheduleSteps] ?? [],
    },
  })

  if (!enabledStructured && Object.keys(changedScheduleFields).length === 0) {
    return null
  }

  return {
    timingMode,
    steps:
      steps.length < 1
        ? null
        : mapCampaignScheduleStepsForApi(steps, timingMode),
  }
}

/**
 * Reverts schedule-related campaign fields after a successful campaign PUT
 * when the follow-up schedule-steps replace fails, so the campaign is not left
 * marked structured without matching steps.
 */
export function getScheduleSaveCompensatingPayload({
  initialValues,
  savedPayload,
}: {
  initialValues: TCampaignBuilderForm
  savedPayload: Record<string, unknown>
}): TCampaignUpdatePayload | null {
  const compensating: TCampaignUpdatePayload = {}

  if (ECampaignBuilderFormKeys.useStructuredSchedule in savedPayload) {
    compensating[ECampaignKeys.useStructuredSchedule] =
      initialValues[ECampaignBuilderFormKeys.useStructuredSchedule]
  }

  if (ECampaignBuilderFormKeys.structuredTimingMode in savedPayload) {
    compensating[ECampaignKeys.structuredTimingMode] =
      initialValues[ECampaignBuilderFormKeys.structuredTimingMode]
  }

  if (ECampaignBuilderFormKeys.maxReachouts in savedPayload) {
    compensating[ECampaignKeys.ofuMaxReachouts] =
      initialValues[ECampaignBuilderFormKeys.maxReachouts]
  }

  return Object.keys(compensating).length > 0 ? compensating : null
}

export const getCampaignBuilderPayload = ({
  agents = [],
  campaign,
  initialValues,
  isSimpleCampaign,
  requiredMetadataVariables = [],
  timezone,
  values,
}: {
  agents?: TAgent[]
  campaign?: TCampaign | null
  initialValues: TCampaignBuilderForm
  isSimpleCampaign: boolean
  requiredMetadataVariables?: string[]
  timezone: string
  values: TCampaignBuilderForm
}) => {
  const fields = isSimpleCampaign
    ? SIMPLE_CAMPAIGN_FIELDS
    : ONGOING_CAMPAIGN_FIELDS
  const currentCampaignValues = pick(values, fields)
  const initialCampaignValues = pick(initialValues, fields)

  const changedFields = getChangedFields({
    currentValues: currentCampaignValues,
    initialValues: initialCampaignValues,
  }) as Record<string, unknown>
  const hasSharedSettingsChange =
    ECampaignBuilderFormKeys.sharedCampaignAgentSettings in changedFields

  const hasSharedIdsChange =
    ECampaignBuilderFormKeys.sharedCampaignAgentIds in changedFields

  // BE still gates outreach evaluation on ofu_maxReachouts; keep it aligned
  // with the structured step count when pacing UI is hidden.
  if (
    !isSimpleCampaign &&
    values[ECampaignBuilderFormKeys.useStructuredSchedule]
  ) {
    const stepCount =
      values[ECampaignBuilderFormKeys.campaignScheduleSteps]?.length ?? 0
    changedFields[ECampaignBuilderFormKeys.maxReachouts] = Math.max(
      stepCount,
      MIN_REACHOUTS,
    )
  }

  const initialSharedAgentIds = new Set(
    (initialValues[ECampaignBuilderFormKeys.sharedCampaignAgentIds] ?? []).map(
      String,
    ),
  )
  const selectedSharedAgentIds = new Set(
    (values[ECampaignBuilderFormKeys.sharedCampaignAgentIds] ?? []).map(String),
  )
  const newlyEnabledSharedAgentIds = new Set(
    Array.from(selectedSharedAgentIds).filter(
      (agentId) => !initialSharedAgentIds.has(agentId),
    ),
  )
  const disabledSharedAgentIds = new Set(
    Array.from(initialSharedAgentIds).filter(
      (agentId) => !selectedSharedAgentIds.has(agentId),
    ),
  )
  const existingJunctionAgentIds = new Set(
    getCampaignAssistantJunctions(campaign).map((junction) =>
      String(junction.agentId || junction.Agent?.id || ''),
    ),
  )

  // Preserve existing behavior for shared-settings-only saves.
  if (hasSharedSettingsChange && !hasSharedIdsChange) {
    changedFields[ECampaignBuilderFormKeys.sharedCampaignAgentIds] =
      values[ECampaignBuilderFormKeys.sharedCampaignAgentIds] ?? []
  }

  const payload = transformBuilderPayloadForApi(changedFields, timezone)

  if (hasSharedIdsChange && Array.isArray(payload[ECampaignKeys.agents])) {
    const enabledAgentsPayload = (
      payload[ECampaignKeys.agents] as TCampaignAgentPayload[]
    ).filter((agentPayload) =>
      newlyEnabledSharedAgentIds.has(String(agentPayload.agentId)),
    )
    const deletedAgentsPayload = Array.from(disabledSharedAgentIds)
      .filter((agentId) => existingJunctionAgentIds.has(agentId))
      .map(
        (agentId): TCampaignAgentPayload => ({
          agentId,
          isDeleted: true,
        }),
      )
    const filteredAgentsPayload = [
      ...enabledAgentsPayload,
      ...deletedAgentsPayload,
    ]

    if (!filteredAgentsPayload.length) {
      delete payload[ECampaignKeys.agents]
    } else {
      payload[ECampaignKeys.agents] = filteredAgentsPayload
    }
  }

  const payloadWithDefaultValues = addDefaultValuesToPayload({
    campaign,
    initialValues,
    isSimpleCampaign,
    payload,
  })

  return {
    payload: appendRequiredAgentMetadataToPayload({
      agents,
      campaign,
      payload: payloadWithDefaultValues,
      requiredMetadataVariables,
      values,
    }),
  }
}

const AGENT_MERGE_FIELD_REGEX = /\[\[([^\]]+)\]\]/g

const CAMPAIGN_BUILDER_AGENT_METADATA_SOURCE_FIELDS = [
  ECampaignBuilderFormKeys.title,
  ECampaignBuilderFormKeys.description,
  ECampaignBuilderFormKeys.ofuGoal,
  ECampaignBuilderFormKeys.additionalInstructions,
  ECampaignBuilderFormKeys.contactEligibilityCriteria,
  ECampaignBuilderFormKeys.phoneIntroMessage,
  ECampaignBuilderFormKeys.phoneVoicemailMessage,
  ECampaignBuilderFormKeys.emailSubject,
  ECampaignBuilderFormKeys.emailContent,
  ECampaignBuilderFormKeys.textMessage,
  ECampaignBuilderFormKeys.cadenceCustom,
  ECampaignBuilderFormKeys.knowledge,
] as const

/** Step content fields that can carry agent `[[…]]` merge tokens. */
const CAMPAIGN_SCHEDULE_STEP_AGENT_METADATA_SOURCE_FIELDS = [
  ECampaignScheduleStepKeys.textMessage,
  ECampaignScheduleStepKeys.emailSubject,
  ECampaignScheduleStepKeys.emailBody,
  ECampaignScheduleStepKeys.callScript,
  ECampaignScheduleStepKeys.voicemailScript,
  ECampaignScheduleStepKeys.outreachInstructionsText,
] as const

export const getUsedAgentMetadataVariables = (
  values: TCampaignBuilderForm,
): Set<string> => {
  const usedAgentMetadataVariables = new Set<string>()
  const addVariablesFromText = (fieldValue: string) => {
    Array.from(fieldValue.matchAll(AGENT_MERGE_FIELD_REGEX)).forEach(
      (match) => {
        const key = match[1]?.trim()
        if (key) {
          usedAgentMetadataVariables.add(key)
        }
      },
    )
  }

  CAMPAIGN_BUILDER_AGENT_METADATA_SOURCE_FIELDS.forEach((fieldKey) => {
    const fieldValue = values[fieldKey]
    if (typeof fieldValue === 'string' && fieldValue) {
      addVariablesFromText(fieldValue)
      return
    }

    if (
      fieldKey === ECampaignBuilderFormKeys.knowledge &&
      Array.isArray(fieldValue)
    ) {
      fieldValue.forEach((knowledgeItem) => {
        const knowledgeText = knowledgeItem?.text
        if (typeof knowledgeText === 'string' && knowledgeText) {
          addVariablesFromText(knowledgeText)
        }
      })
    }
  })

  // Structured cadence steps pin their own outreach copy; tokens used only
  // there must still become required agentMetadataKeys so Growth can resolve
  // them at send time (and so simulation autofill has values to apply).
  const scheduleSteps =
    values[ECampaignBuilderFormKeys.campaignScheduleSteps] ?? []
  scheduleSteps.forEach((step) => {
    CAMPAIGN_SCHEDULE_STEP_AGENT_METADATA_SOURCE_FIELDS.forEach((fieldKey) => {
      const fieldValue = step?.[fieldKey]
      if (typeof fieldValue === 'string' && fieldValue) {
        addVariablesFromText(fieldValue)
      }
    })
  })

  return usedAgentMetadataVariables
}

export function getCampaignAgentMetadataKeys(
  values: TCampaignBuilderForm,
): TMergeField[] {
  const predefinedFriendlyNameByVariable = new Map(
    AGENT_MERGE_FIELDS.map((field) => [
      getAgentMetadataVariable(String(field.value)),
      field.label,
    ]),
  )
  const defaultAgentMetadataKeys = AGENT_MERGE_FIELDS.map((f) => f.value)
  const usedAgentMetadataVariables = getUsedAgentMetadataVariables(values)
  const currentAgentMetadataKeys = [
    ...defaultAgentMetadataKeys,
    ...(values[ECampaignBuilderFormKeys.agentMetadataKeys] || []),
  ]
  const mergeFieldsByVariable = new Map<string, TMergeField>()

  const upsertMergeField = ({
    key,
    preferFriendlyName = false,
  }: {
    key: string
    preferFriendlyName?: boolean
  }) => {
    const normalizedKey = key?.trim()
    if (!normalizedKey) return

    const variable = getAgentMetadataVariable(normalizedKey)
    if (!variable) return

    const currentMergeField = mergeFieldsByVariable.get(variable)
    const shouldUpdateFriendlyName =
      preferFriendlyName &&
      (!currentMergeField ||
        currentMergeField.friendlyName === currentMergeField.variable)
    const defaultFriendlyName =
      predefinedFriendlyNameByVariable.get(variable) || normalizedKey
    const friendlyName = shouldUpdateFriendlyName
      ? defaultFriendlyName
      : (currentMergeField?.friendlyName ?? defaultFriendlyName)

    mergeFieldsByVariable.set(variable, {
      friendlyName,
      variable,
      required:
        Boolean(currentMergeField?.required) ||
        usedAgentMetadataVariables.has(variable) ||
        usedAgentMetadataVariables.has(normalizedKey),
    })
  }

  currentAgentMetadataKeys.forEach((key) => {
    upsertMergeField({ key, preferFriendlyName: true })
  })

  usedAgentMetadataVariables.forEach((usedVariable) => {
    upsertMergeField({ key: usedVariable })
  })

  return Array.from(mergeFieldsByVariable.values()).filter((mergeField) =>
    Boolean(mergeField.required),
  )
}

export function buildCampaignForBuilderSimulate({
  campaign,
  values,
  timezone,
  isSimpleCampaign,
  agents = [],
}: {
  campaign?: TCampaign | null
  values: TCampaignBuilderForm
  timezone: string
  isSimpleCampaign: boolean
  agents?: TAgent[]
}): Record<string, unknown> {
  const fields = isSimpleCampaign
    ? SIMPLE_CAMPAIGN_FIELDS
    : ONGOING_CAMPAIGN_FIELDS
  let payload: Record<string, unknown> = transformBuilderPayloadForApi(
    {
      ...pick(values, fields as unknown as (keyof TCampaignBuilderForm)[]),
    },
    timezone,
  )

  if (
    !isSimpleCampaign &&
    values[ECampaignBuilderFormKeys.useStructuredSchedule]
  ) {
    const formSteps =
      values[ECampaignBuilderFormKeys.campaignScheduleSteps] ?? []
    payload[ECampaignBuilderFormKeys.maxReachouts] = Math.max(
      formSteps.length,
      MIN_REACHOUTS,
    )

    // Simulate accepts in-memory steps; campaign PUT does not persist them.
    if (formSteps.length > 0) {
      const apiSteps = mapCampaignScheduleStepsForApi(
        formSteps,
        values[ECampaignBuilderFormKeys.structuredTimingMode],
      )
      payload[ECampaignBuilderFormKeys.campaignScheduleSteps] = apiSteps
      payload.scheduleSteps = apiSteps
    }
  }

  // Ensure simulation always uses canonical metadata field definitions from
  // current builder state (same shape as save payload).
  const agentMetadataKeys = getCampaignAgentMetadataKeys(values)
  payload[ECampaignKeys.agentMetadataKeys] = agentMetadataKeys
  payload = appendRequiredAgentMetadataToPayload({
    agents,
    campaign,
    payload,
    requiredMetadataVariables: agentMetadataKeys
      .map((field) => getAgentMetadataVariable(String(field.variable || '')))
      .filter(Boolean),
    values,
  })

  const existingJunctions = ((campaign?.[
    ECampaignKeys.campaignAgentJunctions
  ] ??
    (campaign as Record<string, unknown> | null | undefined)
      ?.CampaignAgentJunction) ||
    []) as TCampaignAgentJunction[]
  const existingJunctionByAgentId = new Map(
    existingJunctions.map((junction) => [
      String(junction.agentId || ''),
      junction,
    ]),
  )
  const agentById = new Map(
    (agents || []).map((agent) => [String(agent[EAgentKeys.id] || ''), agent]),
  )
  const payloadAgents = (payload[ECampaignKeys.agents] ||
    []) as TCampaignAgentPayload[]
  const simulationJunctions = payloadAgents.map((agentPayload) => {
    const agentId = String(agentPayload.agentId || '')
    const existingJunction = existingJunctionByAgentId.get(agentId)
    const liveAgent = agentById.get(agentId)
    const agentData = existingJunction?.Agent
      ? undefined
      : liveAgent || undefined

    return {
      ...existingJunction,
      agentId,
      agentCampaignKnowledge: agentPayload.agentCampaignKnowledge ?? [],
      agentCampaignRules: agentPayload.agentCampaignRules ?? null,
      agentMetadata: agentPayload.agentMetadata ?? {},
      ...(agentData ? { Agent: agentData } : {}),
      status:
        existingJunction?.status || ECampaignAgentJunctionStatus.notStarted,
    } as TCampaignAgentJunction
  })

  if (simulationJunctions.length > 0) {
    payload.CampaignAgentJunction = simulationJunctions
  }
  // Keep simulation payload aligned with campaign manager behavior:
  // `CampaignAgentJunction` is the source of truth for assistant-level data.
  // Sending both `agents` and junctions can cause backend precedence issues.
  if (ECampaignKeys.agents in payload) {
    delete payload[ECampaignKeys.agents]
  }

  const baseCampaign = campaign ? { ...campaign } : {}
  delete (baseCampaign as Record<string, unknown>)[
    ECampaignKeys.campaignAgentJunctions
  ]
  delete (baseCampaign as Record<string, unknown>)[ECampaignKeys.agents]
  delete (payload as Record<string, unknown>)[
    ECampaignKeys.campaignAgentJunctions
  ]

  return {
    ...baseCampaign,
    ...payload,
  }
}

export const getCampaignExecutionType = (campaign?: TCampaign | null) => {
  return (
    campaign?.[ECampaignKeys.executionMode] || ECampaignExecutionMode.scheduled
  )
}

/**
 * On a live, high-audience campaign, campaignType is always locked
 * server-side (CampaignService.update), and startDate is locked whenever the
 * campaign already has one set — its schedule was computed from that date at
 * go-live, so changing it (even to another future date) would desync every
 * scheduled outreach. executionMode is locked for one-time campaigns, whose
 * single outreach is already committed once live. Every other field remains
 * editable and applies to future outreach only. Returns the subset of the
 * changed payload keys that are actually locked, so callers can block just
 * those.
 */
export function getLiveEditLockedKeyChanges(
  payloadKeys: string[],
  {
    isLiveHighAudienceCampaign,
    isStartDateLocked,
    isExecutionModeLocked,
  }: {
    isLiveHighAudienceCampaign: boolean
    isStartDateLocked: boolean
    isExecutionModeLocked: boolean
  },
): string[] {
  if (!isLiveHighAudienceCampaign) {
    return []
  }

  return payloadKeys.filter((key) => {
    if (key === ECampaignKeys.campaignType) {
      return true
    }
    if (key === ECampaignKeys.startDate) {
      return isStartDateLocked
    }
    if (key === ECampaignKeys.executionMode) {
      return isExecutionModeLocked
    }
    return false
  })
}
